/*
 * Class for executing attribute/selector changes.
 * @module
 */
var _ = require('optimizely-client-fn');
var PromiseEnv = require('core/lib/promise').Promise;

var actionTypes = require('core/constants/action_types');
var ApiEvents = require('core/api_events');
var constants = require('core/action/constants');
var createError = require('core/utils/custom_error').create;
var dataHelpers = require('core/action/element_data');
var deferred = require('core/utils/deferred');
var DirectiveStore = require('core/di').get('stores/directive');
var DocumentEnv = require('core/env/document');
var enums = require('core/action/enums');
var Flux = require('core/flux');
var Logger = require('core/utils/logger');
var MutationObserverEnv = require('core/env/mutation_observer');
var RateMeter = require('core/utils/rate_meter');
var timingHelpers = require('core/action/timing_helpers');
var WindowEnv = require('core/env/window');

var fns = require('./fns');

var api = require('api/utils').create();

var MUTATION_OBSERVER_CONFIG = {
  attributes: true,
  childList: true,
  subtree: true,
  characterData: true
};

var CHANGE_APPLICATION_RATE = {
  MOVING_WINDOW_MILLISECONDS: __CHANGE_APPLICATION_RATE__.MOVING_WINDOW_MILLISECONDS,
  MAX_MACROTASKS_IN_MOVING_WINDOW: __CHANGE_APPLICATION_RATE__.MAX_MACROTASKS_IN_MOVING_WINDOW,
};

var REAPPLY_ATTRIBUTE_CHANGES = __REAPPLY_ATTRIBUTE_CHANGES__;


var ChangeOverheatError = createError('ChangeOverheatError');


/**
 * @constructor
 *
 * @param {AttributeChange} change
 * @param {ChangeConfig} config
 */
function AttributeChange(change:AttributeChange, config:ChangeConfig) {
  this.change = _.cloneDeep(change);
  this.change = fns.transformVisibilityAttributesToCSS(this.change);

  this.identifier = config.identifier;
  this.startTime = config.startTime;
  this.disconnectObserverQueue = [];

  if (DirectiveStore.shouldObserveChangesIndefinitely()) {
    Flux.dispatch(actionTypes.INITIALIZE_CHANGE_METRICS);

    this.rateMeter = new RateMeter(CHANGE_APPLICATION_RATE.MOVING_WINDOW_MILLISECONDS);

    // Set a rate limit on change applications.
    var maxRate = _.isNull(CHANGE_APPLICATION_RATE.MAX_MACROTASKS_IN_MOVING_WINDOW) ? Number.POSITIVE_INFINITY : CHANGE_APPLICATION_RATE.MAX_MACROTASKS_IN_MOVING_WINDOW;
    this.rateMeter.addListener(maxRate, _.bind(function() {
      Logger.warn('AttributeChange', this, 'has overheated and will no longer apply or reapply');
      this.cancel();
      Flux.dispatch(actionTypes.RECORD_CHANGE_OVERHEATED);
      ApiEvents.emitError(new ChangeOverheatError('Change ' + this.identifier + ' has overheated'), {
        layerId: config.action && config.action.layerId,
        experimentId: config.action && config.action.experimentId,
        variationId: config.action && config.action.variationId,
        changeId: change.id,
        changeType: change.type,
        movingWindowMilliseconds: CHANGE_APPLICATION_RATE.MOVING_WINDOW_MILLISECONDS,
        maxMacroTasksInMovingWindow: maxRate,
      });
    }, this));

    // Track the temperature in RUM (as it reaches more and more milestones).
    var maxRateToTrackInRum = Math.min(maxRate, 50);
    for (var rate = 0; rate <= maxRateToTrackInRum; rate++) {
      this.rateMeter.addListener(rate, _.partial(function(closureRate) {
        Flux.dispatch(actionTypes.RECORD_CHANGE_MACROTASK_RATE, {
          changeMacrotaskRate: closureRate,
        });
      }, rate));
    }
  }

  /**
   * Flipped to true if/when this change applier instance is cancelled.
   * Once that occurs, this instance cannot apply or reapply to any more
   * elements, and callers will need to construct a new instance if they
   * want the change in question to start applying again.
   * Callers wishing to undo this change are expected to first cancel
   * the change to prevent it from applying after it is undone.
   * @type {boolean}
   */
  this.cancelled = false;
}

/**
 * Setup the change to observe the given selector.
 */
AttributeChange.prototype.apply = function () {
  this.applyDeferred = deferred();
  // If we have an invalid selector, the api will throw an error
  try {
    if (_.isEmpty(this.change.attributes) && _.isEmpty(this.change.css)) {
      Logger.debug('Not applying empty AttributeChange');
      this.applyDeferred.resolve();
      return this.applyDeferred;
    }

    var onTimeoutFn = _.partial(this.applyDeferred.reject, new Error('Unable to find selector.'));
    var observeSelectorOptions = {} as any;
    if (DirectiveStore.shouldObserveChangesUntilTimeout()) {
      observeSelectorOptions = {
        timeout: _.partial(timingHelpers.isTimedOut, this.startTime),
        onTimeout: onTimeoutFn
      };
    } else if (DirectiveStore.isEditor()) {
      // Continue to observe indefinitely and apply indefinitely, but ensure the appliedDeferred promise times out.
      // Else we'll freeze the editor if a matching element can't be found. (WEB-2512)
      api.waitUntil(_.partial(timingHelpers.isTimedOut, this.startTime))
        .then(onTimeoutFn, onTimeoutFn);
    } else {
      // Continue to observe indefinitely and apply indefinitely.
      // The appliedDeferred will remain pending (and therefore will block any dependent changes)
      // until its first application.
    }

    this.unobserveSelector = api.observeSelector(
      this.change.selector,
      _.bind(this.maybeApplyToElement, this),
      observeSelectorOptions
    );

    // there is a short timeout on observeSelector that causes flickering in the editor
    // due to rapid apply/undo, so make sure to apply synchronously as well.
    var currentlyPresentNodes = DocumentEnv.querySelectorAll(this.change.selector);
    _.each(currentlyPresentNodes, _.bind(this.maybeApplyToElement, this));
  } catch (e) {
    this.applyDeferred.reject(e);
  }
  return this.applyDeferred;
};

/**
 * Apply the change to the individual element provided by observeSelector,
 * assuming we haven't already done so before.
 *
 * @param {HTMLElement} element - The element which triggered the change.
 */
AttributeChange.prototype.maybeApplyToElement = function(element:HTMLElement) {
  var markingAttribute = constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id;

  // Don't apply changes to the same element more than once
  if (element.hasAttribute(markingAttribute)) {
    Logger.debug('AttributeChange not being applied. Element already changed, or ' +
                 'is a child of an element that was changed by this AttributeChange. ' + element);
    this.applyDeferred.resolve();
    return;
  }

  if (this.rateMeter) {
    this.rateMeter.countCurrentTick();
  }

  var applyOrReapplyToElement = _.bind(this.applyOrReapplyToElement, this, element, markingAttribute);
  applyOrReapplyToElement(false); // initial apply

  if (REAPPLY_ATTRIBUTE_CHANGES) {
    var connectOrReconnectTheReapplyObserver = _.bind(function() {
      var reapplyCallback = _.bind(function() {
        // It's possible (and may be likely in the editor) that this change was cancelled synchronously
        // after it was applied, or generally so soon after application that this application-spawned
        // microtask erroneously executes after the change has been canceled. We definitely don't
        // want to start observing and reapplying at this point.
        if (!this.cancelled) {
          // Note that it's possible that element is not in the DOM, but even if that is the case,
          // we should reapply changes to it because we won't get a chance to do so if/when the element
          // is attached to the document (our reapplier wouldn't be alerted, and our initial applier
          // would skip the element since it's already marked).
          MutationObserverEnv.observe(observer, element, MUTATION_OBSERVER_CONFIG);
        }
      }, this);
      if (__EXPERIMENTAL_REAPPLY_MICROTASK__) {
        // Since MutationObserver callbacks are scanned for (and called in) microtasks,
        // using our own microtask to (re)connect the reapply observer
        // allows us to avoid mutual recursion between this attribute_change (re)application
        // and any other changes that might (re)apply when we reapply this change.
        //
        // NOTE: such mutual recursion is only avoided if the other change applies _immediately_
        // in MutationObserver microtask. If it delays its application to a later task,
        // then our reapply observer would already be reconnected.
        //
        // For more info, see:
        //   https://stackoverflow.com/questions/14564617/when-are-mutationobserver-callbacks-fired/34311648#34311648
        return PromiseEnv.resolve().then(reapplyCallback);
      } else {
        // Since MutationObserver callbacks are scanned for (and called in) microtasks, a setTimeout
        // (macrotask) before we (re)connect is necessary and sufficient to avoid mutual recursion
        // between this attribute_change (re)application and any other changes that might (re)apply
        // when we reapply this change.
        return WindowEnv.setTimeout(reapplyCallback);
      }
    }, this);

    // Observe this element so we can reapply our changes when its attributes or children change.
    var observer = MutationObserverEnv.create(_.bind(function() {
      if (this.rateMeter) {
        this.rateMeter.countCurrentTick();
      }

      observer.disconnect();
      applyOrReapplyToElement(true); // reapply
      connectOrReconnectTheReapplyObserver();
    }, this));

    connectOrReconnectTheReapplyObserver();
    this.disconnectObserverQueue.push(
      _.bind(observer.disconnect, observer)
    );
  }

  this.applyDeferred.resolve();
};

/**
 * For a given element, apply every change.attributes and change.css change to
 * the element, and mark the element with an attribute and undo data.
 * @param element
 * @param markingAttribute
 */
AttributeChange.prototype.applyOrReapplyToElement = function(element, markingAttribute, reapplyChange = false) {
  var elementChangeData = {} as any;
  _.forOwn(this.change.attributes, function (changeValue, changeType) {
    switch (changeType) {
      case enums.selectorChangeType.CLASS:
        if (!_.isUndefined(element.className)) {
          elementChangeData[enums.selectorChangeType.CLASS] = element.className;
          element.className = changeValue;
        }
        break;
      case enums.selectorChangeType.HREF:
        if (!_.isUndefined(element.href)) {
          elementChangeData[enums.selectorChangeType.HREF] = element.href;
          element.href = changeValue;
        }
        break;
      case enums.selectorChangeType.HTML:
        if (!_.isUndefined(element.innerHTML)) {
          elementChangeData[enums.selectorChangeType.HTML] = element.innerHTML;
          element.innerHTML = changeValue;

          // Ensure that any content that's under the change element cannot
          // recursively trigger the change on itself.
          _.each(DocumentEnv.childrenOf(element), function (e) {
            e.setAttribute(markingAttribute, '');
          });
        }
        break;
      case enums.selectorChangeType.SRC:
        if (!_.isUndefined(element.src)) {
          elementChangeData[enums.selectorChangeType.SRC] = element.src;
          element.src = changeValue;
        }
        break;
      case enums.selectorChangeType.STYLE:
        // No-op break from switch, avoiding "Unrecognized attribute" error
        break;
      case enums.selectorChangeType.TEXT:
        if (!_.isUndefined(element.textContent)) {
          elementChangeData[enums.selectorChangeType.TEXT] = element.textContent;
          element.textContent = changeValue;
        }
        break;
      default:
        throw new Error('Unrecognized attribute: ' + changeType);
    }
  });

  // If style changes were made, store original styles, compute new ones and add to the element
  var stylesToSet = fns.createStylesFromChange(element.style.cssText, this.change);
  if (_.isString(stylesToSet)) {
    elementChangeData[enums.selectorChangeType.STYLE] = element.style.cssText;
    element.style.cssText = stylesToSet;
  }

  // Always set the data attribute on the element, regardless of the number of change attributes
  // If this is a reapplication, we don't want to overwrite the data attribute
  element.setAttribute(markingAttribute, '');
  if (!reapplyChange || !dataHelpers.hasData(element)) {
    dataHelpers.setData(element, this.change.id, this.identifier, elementChangeData);
  }
};

/**
 * If this change is still being observed, cancel any future
 * invocations of it by invoking observeSelector's cancel function.
 */
AttributeChange.prototype.cancel = function () {
  this.cancelled = true;
  if (this.unobserveSelector) {
    this.unobserveSelector();
  }
  _.each(this.disconnectObserverQueue, function(disconnectFn) {
    try {
      disconnectFn();
    } catch(e) {
      // Our node may have been deleted, so no-op
    }
  });
};

if (__UNDO_CHANGES_UPON_VIEW_DEACTIVATION__ || __TEST__) {
  /**
   * Undo this change for all elements which have been marked for this changeId.
   *
   * This is done by leveraging the data stored via setData when applyOrReapplyToElement applied the change.
   */
  AttributeChange.prototype.undo = function () {
    var elementList = DocumentEnv.querySelectorAll('[' + constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id + ']');
    _.each(elementList, _.bind(function (element) {
      var elementChangeData = dataHelpers.getData(element, this.change.id, this.identifier);
      if (elementChangeData) {
        _.forOwn(elementChangeData, function (changeValue, changeType) {
          switch (changeType) {
            case enums.selectorChangeType.CLASS:
              element.className = changeValue;
              break;
            case enums.selectorChangeType.HREF:
              element.href = changeValue;
              break;
            case enums.selectorChangeType.HTML:
              element.innerHTML = changeValue;
              break;
            case enums.selectorChangeType.SRC:
              element.src = changeValue;
              break;
            case enums.selectorChangeType.STYLE:
              element.style.cssText = changeValue;
              break;
            case enums.selectorChangeType.TEXT:
              element.textContent = changeValue;
              break;
            case enums.selectorChangeType.HIDE:
              element.style.visibility = changeValue;
              break;
            case enums.selectorChangeType.REMOVE:
              element.style.display = changeValue;
              break;
            default:
              throw new Error('Unrecognized attribute: ' + changeType);
          }
        });
      }
      element.removeAttribute(constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id);
      dataHelpers.removeData(element, this.change.id, this.identifier);
    }, this));
    return deferred().resolve(enums.changeState.UNAPPLIED);
  };
}

/**
 * PluginManager registration method
 * @param {PluginManager} PluginManager
 */
module.exports = function(PluginManager:PluginManagerClass) {
  PluginManager.registerChangeApplier(enums.changeType.ATTRIBUTE, AttributeChange);
};

if (__TEST__) {
  module.exports.plugin = AttributeChange;

  module.exports.ChangeOverheatError = ChangeOverheatError;

  module.exports.getChangeApplicationRate = function() {
    return CHANGE_APPLICATION_RATE;
  };

  module.exports.setChangeApplicationRate = function(changeApplicationRate) {
    CHANGE_APPLICATION_RATE = changeApplicationRate;
  };

  module.exports.getReapplyAttributeChanges = function() {
    return REAPPLY_ATTRIBUTE_CHANGES;
  };

  module.exports.setReapplyAttributeChanges = function(reapply) {
    REAPPLY_ATTRIBUTE_CHANGES = reapply;
  };
}
